<script>
import { debounce } from "debounce";
import LoadingDialog from "./components/loading-dialog.vue";
import Toast from "@/components/Toast.vue";

export default {
  name: "App",

  components: {
    LoadingDialog, Toast
  },

  data: () => ({
    isLocale: true,
  }),

  computed: {
    snackbar: (vm) => vm.$store.getters.GET_SNACKBAR,
    isLoading: (vm) => vm.$store.getters.GET_LOADING,
  },

  beforeMount() {
    this.onResize();
  },

  watch: {
    async "$i18n.locale"() {
      this.isLocale = false;
      await this.$store.dispatch("auth/INIT_USER");
      this.isLocale = true;
    },
  },

  methods: {
    onResize: debounce(function () {
      const width = window.innerWidth || 0;
      const height = window.innerHeight || 0;
      this.$store.dispatch("SET_SIZE", { width, height });
    }, 800),
  },
};
</script>

<template>
  <v-app v-resize="onResize">
    <div id="_dont_remove__text_size"></div>

    <!-- If locale is ready -->
    <template v-if="isLocale">
      <router-view :key="$route.fullPath" />
      <Toast />
    </template>

    <!-- While locale is changing -->
    <v-container v-else style="height: 400px">
      <v-row class="fill-height" align-content="center" justify="center">
        <v-col class="subtitle-1 text-center" cols="12">
          {{ $t("Locale is changing") }}...
        </v-col>
        <v-col cols="6">
          <v-progress-linear color="accent" indeterminate rounded height="6" />
        </v-col>
      </v-row>
    </v-container>

    <!-- Snackbar -->
    <v-snackbar
      top
      v-model="snackbar.isShow"
      :color="snackbar.color"
      :timeout="8500"
    >
      <div v-html="snackbar.text" />
      <template slot="action">
        <v-btn
          icon
          :title="$t('Close')"
          class="textTansform_none"
          dark
          text
          @click="snackbar.isShow = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Loading overlay -->
    <LoadingDialog :overlay="isLoading" />
    <toast />
  </v-app>
</template>

<style lang="scss">
@import url("./assets/password/stylesheet.css");
@import url("./assets/fa-icons/style.css");

/* Brand display font (same files usat.uz serves, public/fonts/made). Single-weight
   face, so the 100-900 range stops the browser faux-bolding it. */
@font-face {
  font-family: "Made";
  src: url("/fonts/made/Made.otf") format("opentype");
  font-weight: 100 900;
  font-style: normal;
  font-display: swap;
}

/* Pages that set body.font-made-page (add-new-student) use the brand font on every
   element, including menus/pickers teleported outside the page; icon fonts, the left navbar
   (drawer) and the top bar keep the app font. */
body.font-made-page .v-application *:not(.v-navigation-drawer):not(.v-navigation-drawer *):not(.top-bar-container):not(.top-bar-container *):not(.v-icon):not([class*="fa-"]):not([class^="icon-"]):not([class*=" icon-"]):not([class^="mdi"]):not([class*=" mdi-"]) {
  font-family: "Made", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif !important;
}

/* Inter (Google Fonts, self-hosted in public/fonts/inter) — variable weight; used by table headers. */
@font-face {
  font-family: "Inter";
  src: url("/fonts/inter/Inter.ttf") format("truetype");
  font-weight: 100 900;
  font-style: normal;
  font-display: swap;
}

/* Search boxes: only the placeholder ("Izlash...") is Made; typed text keeps the app font. */
.v-application .search-input input::placeholder,
.v-application input[placeholder="Izlash..."]::placeholder,
.v-application input[placeholder="Search..."]::placeholder,
.v-application input[placeholder="Поиск..."]::placeholder {
  font-family: "Made", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif !important;
}

/* /groups toolbar row (search, Filterlar, EXPORT, GURUH QO'SHISH): Made end to end, icon fonts spared. */
.v-application .group-page .elevated-toolbar,
.v-application .group-page .elevated-toolbar *:not(.v-icon):not([class*="fa-"]):not([class^="icon-"]):not([class*=" icon-"]):not([class^="mdi"]):not([class*=" mdi-"]) {
  font-family: "Made", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif !important;
}

/* "USTUNLAR" and "TALABA QO'SHISH": the Columns / add-student buttons (modern-button + font-made)
   and the matching titles in the applications list. */
.v-application .v-btn.modern-button.font-made,
.v-application .v-btn.modern-button.font-made .v-btn__content,
.v-application .columns-title,
.v-application .create-title {
  text-transform: uppercase !important;
}

/* Status tab row (Barchasi, To'lovsiz, Qisman ... Excel orqali yuklangan): full uppercase. */
.v-application .v-tab.tab-upper {
  text-transform: uppercase !important;
}

/* "Orqaga" buttons on the detail (-show) pages: the button and its text span, not just the root. */
.v-application .modern-back-btn,
.v-application .modern-back-btn .v-btn__content,
.v-application .modern-back-btn span {
  font-family: "Made", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif !important;
}

/* "YANGI SHARTNOMA" / "FILTRLAR" on the contract detail history panels: the components' own
   scoped rules say `text-transform: none !important`, so this needs the extra specificity. */
.v-application .credit-history .btn-create,
.v-application .credit-history .btn-filter,
.v-application .dorm-history .btn-create,
.v-application .dorm-history .btn-filter {
  text-transform: uppercase !important;
}

/* "Ma'lumot yo'q" (No data) empty-table messages. */
.v-application .v-data-table__empty-wrapper,
.v-application .v-data-table__empty-wrapper td,
.v-application td.empty-row,
.v-application .tc-empty {
  color: #8a94a6 !important;
  font-family: "Made", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif !important;
}

/* "YANGI SHARTNOMA": flat, no shadow in any state (the panels' scoped rules add one on rest/hover/active). */
.v-application .credit-history .btn-create,
.v-application .credit-history .btn-create:hover,
.v-application .credit-history .btn-create:focus,
.v-application .credit-history .btn-create:active,
.v-application .dorm-history .btn-create,
.v-application .dorm-history .btn-create:hover,
.v-application .dorm-history .btn-create:focus,
.v-application .dorm-history .btn-create:active {
  box-shadow: none !important;
}

/* History titles: "Kredit shartnoma tarixi" / "Yotoqxona shartnoma tarixi" panel headings and the
   "Kredit tarixi" / "Yotoqxona" tab headings on the contract detail pages. */
.v-application .credit-history .heading .title,
.v-application .brand-title {
  font-family: 'Made' !important;
  margin-bottom: 12px;
}

/* Bulk-action buttons (Tanlanganlarni o'chirish, Ketgan / Nofaol qilib belgilash, Guruh biriktirish):
   brand font (via font-made) and full uppercase. */
.v-application .upper-btn,
.v-application .upper-btn .v-btn__content {
  text-transform: uppercase !important;
}

/* The group wrapper only exists while rows are selected (v-if). It is `display: contents`, so its
   buttons stay direct items of the toolbar row, right after EXPORT, wrapping like any other
   toolbar button. They all start the same animation at once, so the group drops in together. */
@keyframes upper-btn-drop-in {
  from { opacity: 0; transform: translateY(-14px); }
  to   { opacity: 1; transform: translateY(0); }
}
.v-application .upper-btn-group {
  display: contents;
}
.v-application .upper-btn-group > * {
  animation: upper-btn-drop-in .32s cubic-bezier(.22, 1, .36, 1) backwards;
}
@media (prefers-reduced-motion: reduce) {
  .v-application .upper-btn-group > * { animation: none; }
}

/* Every data-table header row (all tables): Inter on the row and everything in it; sort/arrow
   icons are spared so they stay icons. */
.v-application .v-data-table-header tr,
.v-application .v-data-table-header tr *:not(.v-icon):not([class*="fa-"]):not([class^="mdi"]):not([class*=" mdi-"]) {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Helvetica, Arial, sans-serif !important;
  font-weight: 500 !important; /* medium */
}

/* Buttons that wear the brand font: add `font-made` to a button, or list it here. */
.v-application .font-made,
.v-application .font-made .v-btn__content,
.v-application .v-btn:has(.export-icon),
.v-application .v-btn:has(.export-icon) .v-btn__content,
.v-application button:has(.export-icon),
.v-application .rmx-add,
.v-application .es-add-btn,
.v-application .rmx-sort-label,
.v-application .rmx-sort button,
.v-application .columns-title,
.v-application .create-title {
  font-family: "Made", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif !important;
}

.justify-content-center {
  justify-content: center;
}

.pdf-table {
  border-collapse: collapse;
  border-color: black;
}

.pdf-ul {
  margin-bottom: 1rem;
}

/* ── Modern scrollbar (light) ── */
* {
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,.18) transparent;
}
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: rgba(0,0,0,.18);
  border-radius: 999px;
}
::-webkit-scrollbar-thumb:hover {
  background: rgba(0,0,0,.32);
}
::-webkit-scrollbar-corner {
  background: transparent;
}

@media (min-width: 1904px) {
  .container {
    max-width: 1185px !important;
  }
  .container--fluid {
    max-width: 100% !important;
  }
}

.v-data-table-header th {
  white-space: nowrap;
}

.transform-none {
  text-transform: none !important;
}

.d-flex {
  display: flex;
}

.w-100 {
  width: 100%;
}

.cursor-pointer {
  cursor: pointer;
}

.-download-guide {
  margin-top: 30px;
  margin-bottom: 70px;
  color: #0096db;
  text-align: center;

  a {
    margin-right: 5px;
    font-weight: 500;
  }
}

.add-faculty-select .v-select__selection {
  color: rgba(0, 0, 0, 0.87) !important;
}

img {
  max-width: 100%;
  max-height: 100%;
}

.h-100 {
  height: 100%;
}

.position-relative {
  position: relative;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  box-shadow: 0 0 0 30px white inset !important;
  -webkit-box-shadow: 0 0 0 30px white inset !important;
}

.theme--dark input:-webkit-autofill,
.theme--dark input:-webkit-autofill:hover,
.theme--dark input:-webkit-autofill:focus,
.theme--dark input:-webkit-autofill:active {
  box-shadow: 0 0 0 30px #1a1b23 inset !important;
  -webkit-box-shadow: 0 0 0 30px #1a1b23 inset !important;
  -webkit-text-fill-color: #e4e5eb !important;
}

/* ── Modern scrollbar (dark) ── */
.theme--dark,
.theme--dark * {
  scrollbar-color: rgba(255,255,255,.22) transparent;
}
.theme--dark::-webkit-scrollbar-track,
.theme--dark *::-webkit-scrollbar-track {
  background: transparent;
}
.theme--dark::-webkit-scrollbar-thumb,
.theme--dark *::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,.22);
  border-radius: 999px;
}
.theme--dark::-webkit-scrollbar-thumb:hover,
.theme--dark *::-webkit-scrollbar-thumb:hover {
  background: rgba(255,255,255,.38);
}
.theme--dark::-webkit-scrollbar-corner,
.theme--dark *::-webkit-scrollbar-corner {
  background: transparent;
}

/* ══════════════════════════════════════════════════════════════
   GLOBAL DARK MODE — Soft, Claude-inspired palette
   Accent: #ffd451 (golden)
   ══════════════════════════════════════════════════════════════
   --dark-bg:       #111318     (app/page background — deep charcoal)
   --dark-surface:  #1a1b23     (cards, panels)
   --dark-elevated: #22232e     (dropdowns, hovers)
   --dark-border:   #2a2c3a     (subtle borders)
   --dark-hover:    #33354a     (hover border)
   --dark-text-1:   #e4e5eb     (primary text)
   --dark-text-2:   #8b8d9e     (secondary text)
   --dark-text-3:   #606275     (muted text)
   --dark-accent:   #ffd451     (golden primary)
   --dark-nav:      #191a24     (navbar background)
   ══════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════
   GLOBAL MODERN DIALOGS — Consistent close button & card style
   ══════════════════════════════════════════════════════════════ */

/* ── Dialog card: rounded corners ── */
.v-dialog > .v-card {
  border-radius: 16px !important;
}

/* ── Dialog toolbar: clean header ── */
.v-dialog > .v-card > .v-toolbar {
  background: transparent !important;
  box-shadow: none !important;
}
.v-dialog > .v-card > .v-toolbar .v-toolbar__title {
  font-size: 18px !important;
  font-weight: 700 !important;
}

/* ── Dialog close button: modern bordered square ── */
.v-dialog > .v-card > .v-toolbar .v-btn--icon,
.v-dialog > .v-card > .v-card__title .v-btn--icon {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  border-radius: 8px !important;
  border: 1px solid #e5e7eb !important;
  background: none !important;
  color: #6b7280 !important;
  transition: all 0.15s !important;
}
.v-dialog > .v-card > .v-toolbar .v-btn--icon:hover,
.v-dialog > .v-card > .v-card__title .v-btn--icon:hover {
  border-color: #d1d5db !important;
  background: #f9fafb !important;
  color: #111318 !important;
}
.v-dialog > .v-card > .v-toolbar .v-btn--icon .v-icon,
.v-dialog > .v-card > .v-card__title .v-btn--icon .v-icon {
  font-size: 20px !important;
  color: inherit !important;
}
/* Remove default icon button ripple circle */
.v-dialog > .v-card > .v-toolbar .v-btn--icon::before,
.v-dialog > .v-card > .v-card__title .v-btn--icon::before {
  display: none !important;
}

/* ── Dark mode dialogs ── */
.theme--dark .v-dialog > .v-card {
  background: #1a1b23 !important;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.4) !important;
}
.theme--dark .v-dialog > .v-card > .v-toolbar .v-toolbar__title {
  color: #e4e5eb !important;
}
.theme--dark .v-dialog > .v-card > .v-toolbar .v-btn--icon,
.theme--dark .v-dialog > .v-card > .v-card__title .v-btn--icon {
  border-color: #2a2c3a !important;
  color: #8b8d9e !important;
}
.theme--dark .v-dialog > .v-card > .v-toolbar .v-btn--icon:hover,
.theme--dark .v-dialog > .v-card > .v-card__title .v-btn--icon:hover {
  border-color: #3a3c4a !important;
  background: #22232e !important;
  color: #e4e5eb !important;
}

/* ══════════════════════════════════════════════════════════════
   GLOBAL MODERN INPUTS — Clean, rounded, consistent
   ══════════════════════════════════════════════════════════════ */

/* ── All outlined inputs: rounded corners ── */
.v-input--outlined fieldset {
  border-radius: 10px !important;
}
.v-input--outlined > .v-input__control > .v-input__slot {
  border-radius: 10px !important;
  min-height: 44px !important;
}
.theme--light .v-input--outlined fieldset {
  border-color: #d0d5dd !important;
  border-width: 1.5px !important;
}
.theme--light .v-input--outlined:hover fieldset {
  border-color: #98a2b3 !important;
}

/* ── Dropdown menu: modern rounded ── */
.v-menu__content {
  border-radius: 10px !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1), 0 1px 4px rgba(0, 0, 0, 0.06) !important;
}
.v-menu__content .v-list {
  border-radius: 10px !important;
  padding: 4px !important;
}
.v-menu__content .v-list-item {
  border-radius: 8px !important;
  min-height: 40px !important;
}

/* ── Smooth transitions ── */
.v-input--outlined fieldset {
  transition: border-color 0.2s ease, border-width 0.2s ease !important;
}
.v-input__icon .v-icon {
  transition: color 0.2s ease !important;
}

/* ══════════════════════════════════════════════════════════════
   GLOBAL INPUT FOCUS — #00274c (light) / #ffd451 (dark)
   ══════════════════════════════════════════════════════════════ */

/* ── Light mode: outlined inputs ── */
.theme--light .v-input--outlined.v-input--is-focused fieldset,
.theme--light .v-input--outlined.v-input--is-focused:hover fieldset {
  border-color: #00274c !important;
}

/* ── Light mode: underlined / filled inputs ── */
.theme--light .v-text-field > .v-input__control > .v-input__slot::after {
  border-color: #00274c !important;
}

/* ── Light mode: focused label color ── */
.theme--light .v-input--is-focused .v-label {
  color: #00274c !important;
}

/* ── Light mode: focused input icon color ── */
.theme--light .v-input--is-focused .v-input__icon .v-icon {
  color: #00274c !important;
}

/* ── Light mode: solo / solo-inverted inputs ── */
.theme--light .v-text-field--solo.v-input--is-focused > .v-input__control > .v-input__slot {
  border: 2px solid #00274c !important;
  box-shadow: none !important;
}

/* ── Light mode: v-select highlighted item ── */
.theme--light .v-list-item--active {
  color: #00274c !important;
}
.theme--light .v-list-item--active::before {
  background-color: #00274c !important;
  opacity: 0.12 !important;
}

.theme--light.v-application {
  background: #fff !important;
}
.theme--light .v-main {
  background: #fff !important;
}
.theme--light .v-main__wrap {
  background: #fff !important;
}

.theme--dark.v-application {
  background: #111318 !important;
}
html.theme--dark,
html.theme--dark body {
  background-color: #111318 !important;
}
.theme--dark .v-main {
  background: #111318 !important;
}
.theme--dark .v-main__wrap {
  background: #111318 !important;
}

/* ── Royal Cards ── */
.theme--dark .royal-card {
  background: #1a1b23 !important;
  border-color: #2a2c3a !important;
}
.theme--dark .royal-card:hover {
  border-color: #33354a !important;
}
.theme--dark .royal-card::after {
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.02), transparent) !important;
}
.theme--dark .royal-title {
  color: #8b8d9e !important;
}
.theme--dark .royal-count {
  color: #e4e5eb !important;
}
.theme--dark .royal-meter {
  background: #2a2c3a !important;
}

/* ── Button Primary (dropdown triggers) ── */
.theme--dark .button-primary {
  background-color: #1a1b23 !important;
  border-color: #2a2c3a !important;
  color: #d5d6de !important;
  box-shadow: none !important;
}
.theme--dark .button-primary:hover {
  background-color: #22232e !important;
  border-color: #33354a !important;
}
.theme--dark .button-primary:active {
  background-color: #111318 !important;
}
.theme--dark .button-primary--selected {
  background-color: #ffd451 !important;
  border-color: #ffd451 !important;
  color: #111318 !important;
}

/* ── Filter Button ── */
.theme--dark .filter-btn,
.theme--dark .v-btn.filter-btn {
  background-color: #22232e !important;
  border-color: #33354a !important;
  color: #d5d6de !important;
}
.theme--dark .filter-btn:hover,
.theme--dark .v-btn.filter-btn:hover {
  background-color: #2a2c3a !important;
}
.theme--dark .filter-btn img {
  filter: brightness(0) invert(1);
  opacity: 0.65;
}

/* ── Remove box-shadow from all filter buttons globally ── */
.button-primary,
.button-primary:hover,
.button-primary:active,
.button-primary--selected,
.filter-btn,
.v-btn.filter-btn,
.filter-btn:hover,
.filter-btn:active,
.filter-btn--dark,
.filter-btn--dark:hover,
.filter-btn--dark:active {
  box-shadow: none !important;
}

/* ── V-Card / V-Sheet ── */
.theme--dark .v-card {
  background-color: #1a1b23 !important;
}
.theme--dark .v-card--outlined {
  border-color: #2a2c3a !important;
}
.theme--dark .v-sheet:not(.v-card):not(.v-toolbar):not(.v-tabs-bar):not(.v-stepper):not(.v-picker):not(.v-btn) {
  background-color: transparent !important;
}

/* ── Generic Buttons ── */
.theme--dark .v-btn.v-btn--has-bg:not(.primary):not(.error):not(.success):not(.warning):not(.accent):not(.v-btn--active):not(.modern-primary):not(.modern-danger):not(.modern-primal):not(.excel-btn):not(.add-btn):not(.edit-btn):not(.delete-btn):not(.circle-btn--export):not(.circle-btn--add):not(.circle-btn--edit):not(.circle-btn--delete):not(.filter-btn):not(.filter-btn--dark):not(.circle-btn--columns-dark):not(.modern-accent):not(.ans-add-btn):not(.columns-apply-btn):not(.edit-toggle-btn):not(.save-btn):not(.action-btn--accept):not(.action-btn--reject):not(.action-btn) {
  background-color: #1a1b23 !important;
  color: #d5d6de !important;
}
.theme--dark .v-btn--outlined {
  border-color: #2a2c3a !important;
  color: #d5d6de !important;
  background: transparent !important;
}
.theme--dark .v-btn--text {
  color: #8b8d9e !important;
}
.theme--dark .v-btn--text:hover {
  color: #e4e5eb !important;
}

/* ── V-btn-toggle ── */
.theme--dark .v-btn-toggle {
  background: #1a1b23 !important;
  border-color: #2a2c3a !important;
}
.theme--dark .v-btn-toggle .v-btn--active {
  background: #ffd451 !important;
  color: #111318 !important;
}

/* ── Data Tables ── */
.theme--dark .v-data-table {
  background-color: #1a1b23 !important;
  color: #d5d6de !important;
}
.theme--dark .v-data-table .v-data-table-header th {
  background-color: #1a1b23 !important;
  color: #8b8d9e !important;
  border-bottom: 1px solid #2a2c3a !important;
}
.theme--dark .v-data-table .v-data-table-header th .v-icon {
  color: #606275 !important;
}
.theme--dark .v-data-table td {
  color: #d5d6de !important;
  border-bottom: 1px solid #1e1f2d !important;
}
.theme--dark .v-data-table tbody tr {
  background-color: #1a1b23 !important;
}
.theme--dark .v-data-table tbody tr:hover {
  background-color: #22232e !important;
}
.theme--dark .v-data-table tbody tr:nth-child(even) {
  background-color: #161720 !important;
}
.theme--dark .v-data-table tbody tr:nth-child(even):hover {
  background-color: #22232e !important;
}
.theme--dark .v-data-table .v-data-footer {
  background-color: #1a1b23 !important;
  border-top: 1px solid #2a2c3a !important;
  color: #8b8d9e !important;
}
.theme--dark .v-data-table .v-data-footer .v-select {
  color: #d5d6de !important;
}
.theme--dark .v-data-table .v-data-footer .v-icon {
  color: #8b8d9e !important;
}

/* ── Simple Tables (permissions, faculties, etc.) ── */
.theme--dark .v-simple-table {
  background-color: #1a1b23 !important;
}
.theme--dark .v-simple-table th {
  color: #8b8d9e !important;
  border-bottom: 1px solid #2a2c3a !important;
}
.theme--dark .v-simple-table td {
  color: #d5d6de !important;
  border-bottom: 1px solid #1e1f2d !important;
}
.theme--dark .v-simple-table tbody tr:hover {
  background-color: #22232e !important;
}

/* ── Tabs ── */
.theme--dark .v-tabs-bar {
  background-color: #1a1b23 !important;
}
.theme--dark .v-tab {
  color: #8b8d9e !important;
}
.theme--dark .v-tab--active {
  color: #e4e5eb !important;
}
.theme--dark .v-tabs-slider {
  background-color: #ffd451 !important;
}

/* ── Dividers ── */
.theme--dark .v-divider {
  border-color: #2a2c3a !important;
}

/* ── Labels ── */
.theme--dark .v-label {
  color: #b0b2be !important;
}

/* ══════════════════════════════════════════════════════════════
   GLOBAL v-checkbox → Navy (#00274d) with white checkmark
   ══════════════════════════════════════════════════════════════ */
.v-input--checkbox .v-input--selection-controls__input {
  width: 20px !important;
  height: 20px !important;
  margin-right: 8px !important;
}
.v-input--checkbox .v-input--selection-controls__input .v-icon {
  font-size: 20px !important;
  width: 20px !important;
  height: 20px !important;
  border: 2px solid #d0d5dd;
  border-radius: 4px;
  background: #fff;
  color: transparent !important;
  transition: all 0.15s ease;
}
.v-input--checkbox .v-input--selection-controls__input .v-icon::before {
  content: "" !important;
  display: block;
  width: 12px;
  height: 12px;
  background: transparent;
}
.v-input--checkbox .v-input--selection-controls__input:hover .v-icon {
  border-color: #00274d;
}
/* checked state */
.v-input--checkbox.v-input--is-label-active .v-input--selection-controls__input .v-icon,
.v-input--checkbox .v-input--selection-controls__input .v-icon.mdi-checkbox-marked,
.v-input--checkbox .v-input--selection-controls__input .v-icon.mdi-minus-box {
  background: #00274d !important;
  border-color: #00274d !important;
  color: transparent !important;
}
.v-input--checkbox.v-input--is-label-active .v-input--selection-controls__input .v-icon::before,
.v-input--checkbox .v-input--selection-controls__input .v-icon.mdi-checkbox-marked::before,
.v-input--checkbox .v-input--selection-controls__input .v-icon.mdi-minus-box::before {
  content: "" !important;
  display: block;
  width: 16px;
  height: 16px;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3.5 8.5L6.5 11.5L12.5 4.5' stroke='%23FFF' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
}
.v-input--checkbox .v-input--selection-controls__input .v-icon.mdi-minus-box::before {
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cline x1='4' y1='8' x2='12' y2='8' stroke='%23FFF' stroke-width='2.2' stroke-linecap='round'/%3E%3C/svg%3E") !important;
}
/* hide ripple */
.v-input--checkbox .v-input--selection-controls__ripple {
  display: none !important;
}

/* ── v-simple-checkbox (used by v-data-table show-select) ── */
.v-simple-checkbox .v-icon {
  font-size: 20px !important;
  width: 20px !important;
  height: 20px !important;
  border: 2px solid #d0d5dd;
  border-radius: 4px;
  background: #fff;
  color: transparent !important;
  transition: all 0.15s ease;
}
.v-simple-checkbox .v-icon::before {
  content: "" !important;
  display: block;
  width: 16px;
  height: 16px;
  background: transparent;
}
.v-simple-checkbox:hover .v-icon {
  border-color: #00274d;
}
.v-simple-checkbox .v-icon.mdi-checkbox-marked,
.v-simple-checkbox .v-icon.mdi-minus-box {
  background: #00274d !important;
  border-color: #00274d !important;
  color: transparent !important;
}
.v-simple-checkbox .v-icon.mdi-checkbox-marked::before,
.v-simple-checkbox .v-icon.mdi-minus-box::before {
  content: "" !important;
  display: block;
  width: 16px;
  height: 16px;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3.5 8.5L6.5 11.5L12.5 4.5' stroke='%23FFF' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
}
.v-simple-checkbox .v-icon.mdi-minus-box::before {
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cline x1='4' y1='8' x2='12' y2='8' stroke='%23FFF' stroke-width='2.2' stroke-linecap='round'/%3E%3C/svg%3E") !important;
}

/* dark mode */
.theme--dark .v-input--checkbox .v-input--selection-controls__input .v-icon {
  background: #22232e;
  border-color: #44466a;
}
.theme--dark .v-input--checkbox .v-input--selection-controls__input:hover .v-icon {
  border-color: #ffd451;
}
.theme--dark .v-input--checkbox.v-input--is-label-active .v-input--selection-controls__input .v-icon,
.theme--dark .v-input--checkbox .v-input--selection-controls__input .v-icon.mdi-checkbox-marked,
.theme--dark .v-input--checkbox .v-input--selection-controls__input .v-icon.mdi-minus-box {
  background: #ffd451 !important;
  border-color: #ffd451 !important;
  color: #111318 !important;
}
.theme--dark .v-input--checkbox .v-input--selection-controls__input .v-icon.mdi-checkbox-marked::before {
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3.5 8.5L6.5 11.5L12.5 4.5' stroke='%23111318' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E") !important;
}
.theme--dark .v-input--checkbox .v-input--selection-controls__input .v-icon.mdi-minus-box::before {
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cline x1='4' y1='8' x2='12' y2='8' stroke='%23111318' stroke-width='2.2' stroke-linecap='round'/%3E%3C/svg%3E") !important;
}
/* v-simple-checkbox dark */
.theme--dark .v-simple-checkbox .v-icon {
  background: #22232e;
  border-color: #44466a;
}
.theme--dark .v-simple-checkbox:hover .v-icon {
  border-color: #ffd451;
}
.theme--dark .v-simple-checkbox .v-icon.mdi-checkbox-marked,
.theme--dark .v-simple-checkbox .v-icon.mdi-minus-box {
  background: #ffd451 !important;
  border-color: #ffd451 !important;
  color: #111318 !important;
}
.theme--dark .v-simple-checkbox .v-icon.mdi-checkbox-marked::before {
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3.5 8.5L6.5 11.5L12.5 4.5' stroke='%23111318' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E") !important;
}
.theme--dark .v-simple-checkbox .v-icon.mdi-minus-box::before {
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg'%3E%3Cline x1='4' y1='8' x2='12' y2='8' stroke='%23111318' stroke-width='2.2' stroke-linecap='round'/%3E%3C/svg%3E") !important;
}

/* ── Text Fields & Inputs ── */
.theme--dark .v-text-field > .v-input__control > .v-input__slot::before {
  border-color: #33354a !important;
}
.theme--dark .v-text-field input,
.theme--dark .v-textarea textarea {
  color: #d5d6de !important;
}
.theme--dark .v-text-field .v-label {
  color: #606275 !important;
}
.theme--dark .v-text-field fieldset,
.theme--dark .v-input--outlined fieldset {
  border-color: #2a2c3a !important;
}
.theme--dark .v-input--outlined:hover fieldset {
  border-color: #33354a !important;
}
/* ── Dark mode: outlined focus ── */
.theme--dark .v-input--outlined.v-input--is-focused fieldset,
.theme--dark .v-input--outlined.v-input--is-focused:hover fieldset {
  border-color: #ffd451 !important;
}
/* ── Dark mode: underlined / filled focus ── */
.theme--dark .v-text-field > .v-input__control > .v-input__slot::after {
  border-color: #ffd451 !important;
}
/* ── Dark mode: focused label ── */
.theme--dark .v-input--is-focused .v-label {
  color: #ffd451 !important;
}
/* ── Dark mode: focused icon ── */
.theme--dark .v-input--is-focused .v-input__icon .v-icon {
  color: #ffd451 !important;
}
/* ── Dark mode: solo focus ── */
.theme--dark .v-text-field--solo.v-input--is-focused > .v-input__control > .v-input__slot {
  border: 2px solid #ffd451 !important;
  box-shadow: none !important;
}
/* ── Dark mode: selected list item ── */
.theme--dark .v-list-item--active {
  color: #ffd451 !important;
}
.theme--dark .v-list-item--active::before {
  background-color: #ffd451 !important;
  opacity: 0.12 !important;
}

.theme--dark .v-select__selection {
  color: #d5d6de !important;
}
.theme--dark .v-input__slot {
  background: transparent !important;
}
.theme--dark .v-text-field--outlined > .v-input__control > .v-input__slot {
  background: #22232e !important;
}
.theme--dark .v-select.v-text-field--outlined > .v-input__control > .v-input__slot,
.theme--dark .v-autocomplete.v-text-field--outlined > .v-input__control > .v-input__slot {
  background: #22232e !important;
}
.theme--dark .v-input__icon .v-icon {
  color: #606275 !important;
}

/* ── Menu Dropdowns ── */
.theme--dark .v-menu__content {
  background: #1a1b23 !important;
  box-shadow: 0 8px 24px rgba(0,0,0,0.35) !important;
  border: 1px solid #2a2c3a !important;
  border-radius: 10px !important;
}
.theme--dark .v-menu__content .v-list {
  border-radius: 10px !important;
  padding: 4px !important;
}
.theme--dark .v-menu__content .v-list-item {
  border-radius: 8px !important;
}
.theme--dark .v-menu__content .v-card {
  background-color: #1a1b23 !important;
}

/* ── Chips / Badges ── */
.theme--dark .v-chip {
  background-color: #22232e !important;
  color: #d5d6de !important;
}
.theme--dark .v-chip--outlined {
  border-color: #2a2c3a !important;
  background: transparent !important;
}

/* ── Pagination (global) ── */
.v-pagination .v-pagination__item {
  border: 1px solid #d0d5dd !important;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05) !important;
  background-color: #fff !important;
  font-weight: 500 !important;
  font-size: 14px !important;
  border-radius: 8px !important;
  min-width: 36px !important;
  height: 36px !important;
}
.v-pagination .v-pagination__item--active,
.v-pagination .v-pagination__item--active.primary,
.theme--light .v-pagination .v-pagination__item--active,
.theme--light .v-pagination .v-pagination__item--active.primary {
  background-color: #ffd451 !important;
  border-color: #f5c542 !important;
  color: #1a1500 !important;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05) !important;
}
.v-pagination .v-pagination__navigation {
  border: 1px solid #d0d5dd !important;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05) !important;
  border-radius: 8px !important;
  background-color: #fff !important;
  min-width: 36px !important;
  height: 36px !important;
}
.v-pagination .v-pagination__more {
  min-width: 36px !important;
  height: 36px !important;
}

/* ── Pagination (dark) ── */
.theme--dark .v-pagination .v-pagination__item {
  background-color: #1a1b23 !important;
  border-color: #2a2c3a !important;
  color: #d5d6de !important;
  box-shadow: none !important;
}
.theme--dark .v-pagination .v-pagination__item--active,
.theme--dark .v-pagination .v-pagination__item--active.primary {
  background-color: #ffd451 !important;
  border-color: #ffd451 !important;
  color: #111318 !important;
}
.theme--dark .v-pagination .v-pagination__navigation {
  background-color: #1a1b23 !important;
  border-color: #2a2c3a !important;
  box-shadow: none !important;
}
.theme--dark .v-pagination .v-pagination__navigation .v-icon {
  color: #8b8d9e !important;
}
.theme--dark .v-pagination .v-pagination__more {
  color: #8b8d9e !important;
}

/* ── Skeleton Loaders ── */
.theme--dark .v-skeleton-loader__image,
.theme--dark .v-skeleton-loader__heading,
.theme--dark .v-skeleton-loader__text {
  background: #22232e !important;
}

/* ── Card Table ── */
.theme--dark .card-table {
  background-color: #1a1b23 !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2) !important;
}

/* ── Toolbar ── */
.theme--dark .v-toolbar {
  background-color: #1a1b23 !important;
}

/* ── Modern Table (contracts list) ── */
.theme--dark .modern-shadow {
  background: #1a1b23 !important;
  box-shadow: 0 2px 12px rgba(0,0,0,0.2) !important;
}
.theme--dark .modern-row:hover {
  background-color: #22232e !important;
}
.theme--dark .modern-table {
  background-color: #1a1b23 !important;
}
.theme--dark .cancel-button {
  color: #8b8d9e !important;
}

/* ── Dialog / Modal ── */
.logout-modal { border-radius: 20px !important; overflow: hidden; }
.theme--dark .v-dialog .v-card {
  background-color: #1a1b23 !important;
  border: 1px solid #2a2c3a;
}
.theme--dark .v-dialog .v-card .v-card__title {
  color: #e4e5eb !important;
}
.theme--dark .v-dialog .v-card .v-card__text {
  color: #b0b2be !important;
}

/* ── V-list (dropdowns) ── */
.theme--dark .v-list {
  background-color: #1a1b23 !important;
}
.theme--dark .v-list-item__title {
  color: #d5d6de !important;
}
.theme--dark .v-list-item:hover {
  background-color: #22232e !important;
}
/* ── Stat cards (Dashboard) ── */
.theme--dark .stat-card {
  background: #1a1b23 !important;
  border-color: #2a2c3a !important;
}
.theme--dark .stat-card:hover {
  box-shadow: 0 8px 20px rgba(0,0,0,0.2) !important;
}
.theme--dark .stat-value {
  color: #e4e5eb !important;
}
.theme--dark .stat-title {
  color: #8b8d9e !important;
}
.theme--dark .stat-icon {
  background: #22232e !important;
  box-shadow: inset 0 0 0 1px #2a2c3a !important;
}

/* ── Progress bars ── */
.theme--dark .v-progress-linear__background {
  background-color: #2a2c3a !important;
}

/* ── Tooltip ── */
.theme--dark .v-tooltip__content {
  background: #22232e !important;
  color: #e4e5eb !important;
  border: 1px solid #2a2c3a;
}

/* ── Expansion panels ── */
.theme--dark .v-expansion-panel {
  background-color: #1a1b23 !important;
}
.theme--dark .v-expansion-panel::before {
  box-shadow: none !important;
}
.theme--dark .v-expansion-panel-header {
  color: #d5d6de !important;
}

/* ── Snackbar ── */
.theme--dark .v-snack__wrapper {
  background-color: #22232e !important;
}

/* ── Generic text color overrides ── */
.theme--dark .text--secondary {
  color: #8b8d9e !important;
}
.theme--dark .v-card__title {
  color: #e4e5eb !important;
}
.theme--dark .v-card__text {
  color: #b0b2be !important;
}
.theme--dark .v-card__subtitle {
  color: #8b8d9e !important;
}

/* ══════════════════════════════════════════════════════════════
   PAGE-SPECIFIC DARK OVERRIDES
   ══════════════════════════════════════════════════════════════ */

/* ── PaymentsExplorer (.ux-payments) ── */
.theme--dark .ux-payments {
  --bg: #111318;
  --glass-from: rgba(30,32,48,.92);
  --glass-to: rgba(30,32,48,.84);
  --card: #1a1b23;
  --line: rgba(255,255,255,.06);
  --muted: rgba(228,229,235,.55);
  --elev-1: 0 1px 0 rgba(0,0,0,.12);
  --elev-2: 0 10px 24px rgba(0,0,0,.25);
  --green-bg: rgba(22,163,74,.12);
  --green-br: rgba(22,163,74,.22);
  --onec-bg: rgba(37,99,235,.12);
  --onec-br: rgba(37,99,235,.22);
  --onec-tx: #93b4f8;
  --manual-bg: rgba(16,185,129,.12);
  --manual-br: rgba(16,185,129,.22);
  --manual-tx: #6ee7b7;
  background: var(--bg);
}
.theme--dark .ux-payments .nav__title {
  color: #e4e5eb;
}
.theme--dark .ux-payments .counter__value {
  color: #e4e5eb;
}
.theme--dark .ux-payments .row {
  background: #1a1b23;
  border-color: rgba(255,255,255,.06);
}
.theme--dark .ux-payments .row:hover {
  border-color: #33354a;
}
.theme--dark .ux-payments .row__amount {
  color: #e4e5eb;
}
.theme--dark .ux-payments .row__sub {
  color: #8b8d9e;
}
.theme--dark .ux-payments .row__details {
  background: #22232e;
  border-color: rgba(255,255,255,.06);
}
.theme--dark .ux-payments .kv .k {
  color: #8b8d9e;
}
.theme--dark .ux-payments .kv .v {
  color: #e4e5eb;
}
.theme--dark .ux-payments .chip--source {
  background: #22232e !important;
  border-color: rgba(255,255,255,.08) !important;
  color: #d5d6de !important;
}
.theme--dark .ux-payments .srow {
  background: #1a1b23;
  border-color: rgba(255,255,255,.06);
}
.theme--dark .ux-payments .npager {
  background: #1a1b23 !important;
  border-color: rgba(255,255,255,.06) !important;
}
.theme--dark .ux-payments .seg {
  background: #22232e;
  border-color: rgba(255,255,255,.06);
}
.theme--dark .ux-payments .seg__btn {
  color: #8b8d9e;
}
.theme--dark .ux-payments .seg__btn.is-active {
  color: #111318;
}
.theme--dark .ux-payments .sk {
  background: #22232e;
  background-image: linear-gradient(90deg, #22232e 0px, #2a2c3a 40px, #22232e 80px);
}
.theme--dark .ux-payments .dropdown-btn {
  background-color: #1a1b23 !important;
  border-color: #2a2c3a !important;
  color: #d5d6de !important;
}
.theme--dark .ux-payments .excel-btn {
  background: #ffd451 !important;
  border-color: #ffd451 !important;
  color: #111318 !important;
}
.theme--dark .ux-payments .custom-snack .v-snackbar__wrapper {
  background: #22232e;
  border-color: #2a2c3a;
}
.theme--dark .ux-payments .custom-snack .v-snack__content {
  color: #e4e5eb !important;
}
.theme--dark .ux-payments .dropdown-btn__active {
  background-color: #22232e !important;
  border-color: #ffd451 !important;
  color: #ffd451 !important;
}
/* Pagination buttons */
.theme--dark .ux-payments .v-pagination .v-pagination__item {
  background: #22232e !important;
  color: #d5d6de !important;
  box-shadow: none !important;
}
.theme--dark .ux-payments .v-pagination .v-pagination__item--active {
  background: #ffd451 !important;
  color: #111318 !important;
  box-shadow: 0 2px 8px rgba(255,212,81,.25) !important;
}
.theme--dark .ux-payments .v-pagination .v-pagination__navigation {
  background: #22232e !important;
  box-shadow: none !important;
}
.theme--dark .ux-payments .v-pagination .v-pagination__navigation .v-icon {
  color: #d5d6de !important;
}
.theme--dark .ux-payments .v-pagination .v-pagination__more {
  color: #8b8d9e !important;
}
/* Date picker dark overrides (global — picker is teleported) */
/* Vuetify 2 puts .theme--dark ON the element, so use both patterns */
.v-picker__body.theme--dark,
.theme--dark .v-picker__body {
  background: #1a1b23 !important;
}
.v-picker__title.theme--dark,
.theme--dark .v-picker__title,
.theme--dark .v-date-picker-title {
  /* Scoped to the dark theme: as a bare .v-date-picker-title it also painted this dark box behind
     the date in every light-theme calendar. */
  background: #22232e !important;
}
.theme--dark .v-date-picker-title,
.theme--dark .v-date-picker-title__date {
  color: #e4e5eb !important;
}
.theme--dark .v-date-picker-title__year {
  color: #8b8d9e !important;
}
.v-date-picker-header.theme--dark,
.theme--dark .v-date-picker-header {
  background: #1a1b23 !important;
}
.theme--dark .v-date-picker-header .v-btn .v-icon {
  color: #d5d6de !important;
}
.theme--dark .v-date-picker-header__value button {
  color: #e4e5eb !important;
}
.v-date-picker-table.theme--dark,
.theme--dark .v-date-picker-table {
  background: #1a1b23 !important;
}
.v-date-picker-table.theme--dark th,
.theme--dark .v-date-picker-table th {
  color: #8b8d9e !important;
}
.theme--dark .v-date-picker-table .v-btn {
  color: #d5d6de !important;
}
.theme--dark .v-date-picker-table .v-btn--active {
  background-color: #ffd451 !important;
  color: #111318 !important;
}
.theme--dark .v-date-picker-table .v-btn--active .v-btn__content {
  color: #111318 !important;
}
.v-picker.theme--dark {
  background: #1a1b23 !important;
  border: 1px solid #2a2c3a;
  border-radius: 14px;
  overflow: hidden;
}
/* Calendar popups of the Kredit / Yotoqxona shartnoma tarixi panels and their payment dialogs
   (v-menu content-class="ux-calendar", 290px = the calendar's own width, so no blank strip): one
   rounded frame on the popup, nothing doubled on the card or the picker inside it. */
.v-menu__content.ux-calendar {
  border: 1px solid #e5e7eb;
  border-radius: 12px !important;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14) !important;
  overflow: hidden;
}
.ux-calendar .date-picker-card,
.ux-calendar .v-picker,
.ux-calendar .v-picker.theme--dark {
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}
/* The selected day in the app's own colour (navy; yellow in the dark theme): Vuetify lays a white
   18% overlay on active buttons, which turned the navy circle slate-blue. */
.ux-calendar .v-date-picker-table .v-btn--active::before {
  opacity: 0 !important;
}
.theme--dark .v-menu__content.ux-calendar {
  border-color: #2a2c3a;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5) !important;
}
/* The date-entry dialogs of the same panels (Yangi shartnoma, Sana / Izoh edit, To'lovni tahrirlash).
   Their light colours are inline styles, so in the dark theme the header, body and footer stayed
   white around dark inputs; !important is what lets the dark theme take them over. The dlg__*
   classes exist only in CreditHistory, DormitoryHistory and their payment panels. */
.theme--dark .dlg__card,
.theme--dark .v-card.dlg {
  background: #1a1b23 !important;
  border-color: #2a2c3a !important;
}
.theme--dark .dlg__head,
.theme--dark .dlg__actions {
  background: #22232e !important;
  border-color: #2a2c3a !important;
}
.theme--dark .dlg__body {
  background: #1a1b23 !important;
}
.theme--dark .dlg__title {
  color: #e4e5eb !important;
}
.theme--dark .dlg__subtitle,
.theme--dark .dlg__subtitle .v-icon,
.theme--dark .dlg__close,
.theme--dark .dlg__close .v-icon {
  color: #8b8d9e !important;
}
.theme--dark .dlg__icon {
  background: #2a2c3a !important;
}
.theme--dark .dlg__icon .v-icon {
  color: #e4e5eb !important;
}
.theme--dark .dlg__actions .v-btn.v-btn--outlined {
  background: #22232e !important;
  border-color: #33354a !important;
  color: #d5d6de !important;
}
.theme--dark .dlg__actions .v-btn.v-btn--outlined .v-icon {
  color: #d5d6de !important;
}
.theme--dark .dlg__actions .v-btn:not(.v-btn--outlined):not(.v-btn--text) {
  background: #ffd451 !important;
  color: #111318 !important;
}
.theme--dark .dlg__actions .v-btn:not(.v-btn--outlined):not(.v-btn--text) .v-icon {
  color: #111318 !important;
}
/* Outlined input fields in nav */
.theme--dark .ux-payments .v-text-field--outlined fieldset {
  border-color: rgba(255,255,255,.10) !important;
}
.theme--dark .ux-payments .v-text-field--outlined:hover fieldset {
  border-color: rgba(255,255,255,.20) !important;
}
.theme--dark .ux-payments .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #ffd451 !important;
}
.theme--dark .ux-payments .v-label {
  color: #8b8d9e !important;
}
.theme--dark .ux-payments .v-text-field__slot input {
  color: #e4e5eb !important;
}
.theme--dark .ux-payments .nav .v-input .v-icon {
  color: #8b8d9e !important;
}
.theme--dark .ux-payments .row__chev .v-icon {
  color: #8b8d9e !important;
}
.theme--dark .ux-payments .excel-btn .v-icon {
  color: #111318 !important;
}
/* Reset button (exclude excel-btn) */
.theme--dark .ux-payments .nav__right > .v-btn--text:not(.excel-btn) {
  color: #8b8d9e !important;
}
.theme--dark .ux-payments .nav__right > .v-btn--text:not(.excel-btn):hover {
  color: #e4e5eb !important;
}
/* Pager arrows in top section */
.theme--dark .ux-payments .pager .v-btn {
  color: #d5d6de !important;
}

/* ── Permissions (.sticky-col) ── */
.theme--dark .sticky-col {
  background: #1a1b23 !important;
}
.theme--dark .permission-table thead th {
  background: #1a1b23 !important;
}

/* ── Faculties (.af-*) ── */
.theme--dark .af-container {
  background: #111318 !important;
}
.theme--dark .af-card {
  background: #1a1b23 !important;
  border-color: #2a2c3a !important;
}
.theme--dark .af-title {
  color: #e4e5eb !important;
}
.theme--dark .af-subtitle {
  color: #8b8d9e !important;
}
.theme--dark .af-btn {
  background: #1a1b23 !important;
  border-color: #2a2c3a !important;
}
.theme--dark .af-btn:hover {
  box-shadow: 0 8px 20px rgba(0,0,0,.2) !important;
}
.theme--dark .af-toolbar {
  color: #e4e5eb;
}
.theme--dark .af-filters {
  background: linear-gradient(180deg, rgba(30,32,48,0.9), rgba(22,23,31,0.9)) !important;
  border-color: #2a2c3a !important;
}
.theme--dark .af-table >>> .v-data-table-header {
  background: linear-gradient(180deg, #1a1b23 75%, rgba(26,27,35,0.85)) !important;
}
.theme--dark .af-table >>> th {
  color: #8b8d9e !important;
  border-bottom-color: #2a2c3a !important;
}
.theme--dark .af-table >>> td {
  color: #d5d6de !important;
}
.theme--dark .af-tbody .af-row:hover {
  background: rgba(255,212,81,0.04) !important;
}
.theme--dark .af-reset {
  color: #8b8d9e !important;
}
.theme--dark .dlg-card,
.theme--dark .del-card {
  background: #1a1b23 !important;
}
.theme--dark .dlg-header,
.theme--dark .del-header {
  border-bottom-color: #2a2c3a !important;
  color: #e4e5eb !important;
}
.theme--dark .dlg-subtitle {
  color: #8b8d9e !important;
}
.theme--dark .dlg-table {
  border-color: #2a2c3a !important;
}
.theme--dark .dlg-table thead th {
  background: #22232e !important;
  color: #8b8d9e !important;
}
.theme--dark .dlg-table td {
  color: #d5d6de !important;
  border-bottom-color: #2a2c3a !important;
}
.theme--dark .dlg-actions,
.theme--dark .del-body {
  border-color: #2a2c3a !important;
}
.theme--dark .del-target .label {
  color: #8b8d9e !important;
}
.theme--dark .prices-table thead th {
  background: #22232e !important;
}
.theme--dark .seg {
  background: #22232e !important;
  border-color: rgba(255,255,255,.06) !important;
}
.theme--dark .seg__btn {
  color: #8b8d9e !important;
}
.theme--dark .seg__btn--active {
  color: #111318 !important;
}
.theme--dark .af-cell-num {
  color: #606275 !important;
}
.theme--dark .sk {
  background: #22232e;
  background-image: linear-gradient(100deg, #22232e 20%, #2a2c3a 40%, #22232e 60%) !important;
}

/* ── Contract Detail (show page) ── */
.theme--dark .modern-back-btn {
  background: linear-gradient(135deg, #1a1b23, #22232e) !important;
  border-color: #2a2c3a !important;
  box-shadow: 0 6px 20px rgba(0,0,0,0.2) !important;
}
.theme--dark .modern-back-btn .v-icon {
  color: #e4e5eb !important;
}
.theme--dark .modern-back-btn span {
  color: #e4e5eb !important;
}
.theme--dark .-wrap {
  background: rgba(30,32,48,0.5) !important;
}
.theme--dark .contract-btn {
  background-color: #1a1b23 !important;
  border-color: #2a2c3a !important;
  color: #d5d6de !important;
}
.theme--dark .contract-btn:hover {
  background-color: #22232e !important;
  border-color: #33354a !important;
}

/* ── Badge overrides (used across multiple pages) ── */
.theme--dark .badge-light {
  background-color: #22232e !important;
  color: #d5d6de !important;
}

/* ── V-alert ── */
.theme--dark .v-alert {
  background-color: #22232e !important;
}

/* ── Accent color buttons in dark mode ── */
.theme--dark .v-btn.accent {
  background-color: #ffd451 !important;
  color: #111318 !important;
}
.theme--dark .v-btn.accent--text {
  color: #ffd451 !important;
  background-color: transparent !important;
}
/* Page title above the tab buttons ("Shaxsiy ma’lumotlar" etc.): plain white, not the accent colour. */
.theme--dark .title.tab-title {
  color: #fff !important;
}

/* (old _cards-wrap overrides removed — replaced by royal-card system) */

/* ── Payments page nav glass override ── */
.theme--dark .ux-payments .nav {
  background: rgba(26,27,35,.92) !important;
  -webkit-backdrop-filter: saturate(1.2) blur(12px) !important;
  backdrop-filter: saturate(1.2) blur(12px) !important;
  border-color: rgba(255,255,255,.06) !important;
}
.theme--dark .ux-payments .list {
  background: #1a1b23 !important;
  border-color: rgba(255,255,255,.06) !important;
}
.theme--dark .ux-payments .counters .counter {
  background: #1a1b23 !important;
  border-color: rgba(255,255,255,.06) !important;
}
.theme--dark .ux-payments .counters .counter__label {
  color: #8b8d9e !important;
}

/* ── Language dropdown modern (light + dark) ── */
.locale-dropdown {
  border-radius: 14px !important;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0,0,0,.10), 0 2px 8px rgba(0,0,0,.05) !important;
  min-width: 260px !important;
}
.locale-dropdown .v-list {
  border-radius: 14px !important;
  padding: 6px !important;
  min-width: 260px !important;
}
.locale-dropdown .v-list-item {
  border-radius: 10px !important;
  min-height: 44px !important;
  padding: 0 14px !important;
  transition: background .12s ease;
}
.locale-dropdown .v-list-item:hover:not(.v-list-item--disabled) {
  background: rgba(0,0,0,.04) !important;
}
.locale-dropdown .v-list-item--disabled {
  background: rgba(0,39,76,.06) !important;
  opacity: 1 !important;
}
.locale-dropdown .v-list-item--disabled .v-list-item__title {
  color: #00274c !important;
  font-weight: 600 !important;
}
.locale-dropdown .v-list-item__title {
  font-size: 14px !important;
  font-weight: 500 !important;
}

/* Dark variant */
.theme--dark .locale-dropdown {
  box-shadow: 0 8px 30px rgba(0,0,0,.35), 0 2px 8px rgba(0,0,0,.2) !important;
}
.theme--dark .locale-dropdown .v-list {
  background: #1a1b23 !important;
  border: 1px solid #2a2c3a !important;
}
.theme--dark .locale-dropdown .v-list-item:hover:not(.v-list-item--disabled) {
  background: #22232e !important;
}
.theme--dark .locale-dropdown .v-list-item--disabled {
  background: rgba(255,212,81,.08) !important;
}
.theme--dark .locale-dropdown .v-list-item--disabled .v-list-item__title {
  color: #ffd451 !important;
}
/* Tick icon colors */
.locale-dropdown .locale-check {
  color: #00274c !important;
}
.theme--dark .locale-dropdown .locale-check {
  color: #ffd451 !important;
}

/* ── Loading indicator accent ── */
.theme--dark .v-progress-linear .v-progress-linear__indeterminate .long,
.theme--dark .v-progress-linear .v-progress-linear__indeterminate .short {
  background-color: #ffd451 !important;
}

.v-card__title {
  word-break: break-word !important;
}

#map.map_uzb text {
  display: none;
}

.border-0 {
  border-radius: 0 !important;
}

.expand-enter-active,
.expand-leave-active {
  transition: height 0.3s ease-in-out;
  transition-property: opacity, height;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  height: 0;
  opacity: 0;
}

.bold--text {
  font-weight: 500;
}

.inherit--text {
  color: inherit !important;
}

.fs-26 {
  font-size: 26px;
}

.text-field-auth {
  border-radius: 3px;

  .v-input__slot {
    background-color: white !important;
    box-shadow: none !important;
  }

  &.error--text {
    .v-input__slot {
      background-color: #fddee2 !important;
      box-shadow: 0 0 8px #fddee2;
    }
  }

  &.v-input--is-disabled {
    opacity: 0.7;
  }
}

.border-0.theme--dark.v-btn.v-btn--disabled.v-btn--has-bg {
  background-color: rgb(31 72 110) !important;
}

.auth-text-field2.v-text-field--enclosed.v-input--dense:not(
    .v-text-field--solo
  ).v-text-field--outlined
  .v-input__append-inner {
  margin-top: 12px;
}

.region-btn {
  height: max-content !important;
  padding: 7px !important;
}

.region-btn * {
  display: flex !important;
  flex-wrap: wrap !important;
  white-space: normal;
  width: max-content !important;
  height: max-content !important;
}

/* Drawer styles */
.v-navigation-drawer {
  background-color: #102b4c;
}

.theme--dark .v-navigation-drawer {
  background-color: #191a24 !important;
  border-right: 1px solid rgba(255,255,255,.06) !important;
}

.v-navigation-drawer__content {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: auto;
  position: relative;
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,.15) transparent;
}

.v-navigation-drawer__content::-webkit-scrollbar {
  width: 4px;
}

.v-navigation-drawer__content::-webkit-scrollbar-track {
  background: transparent;
}

.v-navigation-drawer__content::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,.15);
  border-radius: 999px;
}

.v-navigation-drawer__content::-webkit-scrollbar-thumb:hover {
  background: rgba(255,255,255,.3);
}

.v-navigation-drawer__content::before,
.v-navigation-drawer__content::after {
  content: '';
  position: sticky;
  left: 0;
  right: 0;
  height: 20px;
  pointer-events: none;
  z-index: 2;
}

.v-navigation-drawer__content::before {
  top: 0;
  background: linear-gradient(to bottom, #102b4c, transparent);
}

.v-navigation-drawer__content::after {
  bottom: 0;
  background: linear-gradient(to top, #102b4c, transparent);
}

.theme--dark .v-navigation-drawer__content::before {
  background: linear-gradient(to bottom, #191a24, transparent);
}

.theme--dark .v-navigation-drawer__content::after {
  background: linear-gradient(to top, #191a24, transparent);
}

/* ─────────────────────────────────────────────────────────────
   Excel-export "frozen" (disabled) state.
   The export buttons paint their brand colour with !important
   (.excel-btn → yellow, .modern-export → green), which overrides
   Vuetify's default disabled fill — so a disabled button still
   looked active. Force an unmistakable greyed-out look in both
   themes. High specificity (.v-application + extra class) + !important
   beats the components' scoped brand rules regardless of load order.
   ───────────────────────────────────────────────────────────── */
.v-application .v-btn.v-btn--disabled.excel-btn,
.v-application .v-btn.v-btn--disabled.modern-export {
  background: #e8eaed !important;
  border-color: #dfe2e6 !important;
  color: #9aa1aa !important;
  box-shadow: none !important;
  opacity: 1 !important;
  cursor: not-allowed !important;
}
.v-application .v-btn.v-btn--disabled.excel-btn img,
.v-application .v-btn.v-btn--disabled.modern-export img,
.v-application .v-btn.v-btn--disabled.excel-btn .v-icon,
.v-application .v-btn.v-btn--disabled.modern-export .v-icon {
  filter: grayscale(1) !important;
  opacity: .5 !important;
}

/* Native exam-results export button */
.er-export-btn:disabled {
  background: #e8eaed !important;
  color: #9aa1aa !important;
  cursor: not-allowed !important;
  opacity: 1 !important;
}
.er-export-btn:disabled img {
  filter: grayscale(1) brightness(.7) !important;
  opacity: .55 !important;
}

/* Dark theme — muted dark grey instead of light */
.theme--dark.v-application .v-btn.v-btn--disabled.excel-btn,
.theme--dark.v-application .v-btn.v-btn--disabled.modern-export,
.theme--dark .er-export-btn:disabled {
  background: #2b2f37 !important;
  border-color: #3a3f49 !important;
  color: #6c727c !important;
}

/* ════════════════════════════════════════════════════════════════
   MOBILE RESPONSIVE FIXES (app-wide). Every rule is inside a
   max-width media query, so it cannot change rendering above the
   breakpoint — desktop is unaffected. Additive only.
   ════════════════════════════════════════════════════════════════ */
@media (max-width: 600px) {
  /* (1) Horizontal-scroll backstop. `clip` (not `hidden`) so it does
     not create a scroll container and cannot break position:sticky. */
  html, body { overflow-x: clip; }

  /* (2) Wide data tables scroll inside their own wrapper instead of
     widening the page (reinforces Vuetify's default; mobile-only). */
  .v-data-table__wrapper {
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch;
  }

  /* (3) Dialog overflow cap — highest-leverage rule. width-prop dialogs
     (ModalsTemplate :width emits inline width:650/800px) overflow phones;
     max-width beats the inline width and repairs every width-prop modal.
     Fullscreen dialogs are excluded so :fullscreen sheets still fill. */
  .v-dialog:not(.v-dialog--fullscreen) {
    max-width: calc(100vw - 24px) !important;
    margin: 12px auto !important;
  }

  /* (4) Gentle container side padding (matches Vuetify's 12px default). */
  .container { padding-left: 12px !important; padding-right: 12px !important; }
}
</style>
